import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { euros, fechaHora, plural } from "../lib/formato";
import {
  Aviso,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  Vacio,
} from "../componentes/piezas";

/**
 * Las solicitudes de prueba que llegan del formulario de la web.
 *
 * Cada una se contesta de un toque: se emite una licencia de una con el plan
 * que toque —la cuota la propone el tramo de los socios que dijeron tener— y
 * se le manda por correo a quien la pidió, con la dirección del alta. Si el
 * correo no está configurado, la licencia se emite igual y aquí se dice que
 * hay que mandársela a mano, con el código a la vista.
 */

interface Solicitud {
  id: string;
  createdAt: string;
  nombreClub: string;
  ciudad: string | null;
  nombre: string;
  email: string;
  telefono: string | null;
  sociosAproximados: number | null;
  mensaje: string | null;
  estado: "PENDIENTE" | "ATENDIDA" | "DESCARTADA";
  atendidaEn: string | null;
  atendidaPor: string | null;
  notas: string | null;
  cuotaSugeridaCentimos: number | null;
  /** Días que lleva el código enviado sin canjear; null si no hay código o ya se usó. */
  diasSinCanjear: number | null;
  licencia: {
    id: string;
    codigo: string;
    estado: string;
    club: { id: string; nombre: string } | null;
  } | null;
}

interface Respuesta {
  solicitudes: Solicitud[];
  resumen: { pendientes: number; sinCanjear: number; dadosDeAlta: number };
  diasDePrueba: number;
  /** A partir de cuántos días sin canjear se marca en ámbar: toca llamar. */
  diasParaInsistir: number;
  correoConfigurado: boolean;
}

const ETIQUETA: Record<Solicitud["estado"], { texto: string; clase: string }> =
  {
    PENDIENTE: { texto: "Sin contestar", clase: "ambar" },
    ATENDIDA: { texto: "Contestada", clase: "verde" },
    DESCARTADA: { texto: "Descartada", clase: "gris" },
  };

export default function Solicitudes() {
  const cliente = useQueryClient();
  const [contestando, setContestando] = useState<Solicitud | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const datos = useQuery({
    queryKey: ["plataforma-solicitudes"],
    queryFn: () => api<Respuesta>("/plataforma/solicitudes"),
  });
  const refrescar = () => {
    void cliente.invalidateQueries({ queryKey: ["plataforma-solicitudes"] });
    // El contador del boton de arriba y la lista de licencias tambien cambian.
    void cliente.invalidateQueries({ queryKey: ["plataforma-clubes"] });
    void cliente.invalidateQueries({ queryKey: ["plataforma-licencias"] });
  };

  async function reenviar(s: Solicitud) {
    setError("");
    setMensaje("");
    try {
      const r = await api<{ codigo: string; correoEnviado: boolean }>(
        `/plataforma/solicitudes/${s.id}/reenviar`,
        { metodo: "POST" },
      );
      setMensaje(
        r.correoEnviado
          ? `Código ${r.codigo} reenviado a ${s.email}.`
          : `El correo no salió. El código es ${r.codigo}: mándaselo tú a ${s.email}.`,
      );
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo reenviar");
    }
  }

  async function descartar(s: Solicitud) {
    const motivo = window.prompt(
      `¿Por qué se descarta la solicitud de ${s.nombreClub}? (opcional)`,
    );
    if (motivo === null) return;
    setError("");
    try {
      await api(`/plataforma/solicitudes/${s.id}/descartar`, {
        metodo: "POST",
        cuerpo: { notas: motivo || null },
      });
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo descartar");
    }
  }

  if (datos.isLoading || !datos.data) return <Cargando />;
  const { solicitudes, resumen, correoConfigurado, diasParaInsistir } =
    datos.data;

  return (
    <div>
      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta="Sin contestar"
          valor={resumen.pendientes}
          pie="Esperan un código"
        />
        <TarjetaDato
          etiqueta="Sin canjear"
          valor={resumen.sinCanjear}
          pie="Tienen el código y no se han dado de alta"
        />
        <TarjetaDato
          etiqueta="Dados de alta"
          valor={resumen.dadosDeAlta}
          pie={`de ${solicitudes.length} que pidieron la prueba`}
        />
      </div>

      {!correoConfigurado &&
        solicitudes.some((s) => s.estado === "PENDIENTE") && (
          <Aviso tipo="atencion">
            El correo no está configurado: al contestar, el código se emite
            igual pero tendrás que mandárselo tú.
          </Aviso>
        )}
      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      <Panel sinCuerpo>
        {solicitudes.length === 0 ? (
          <Vacio>
            Todavía no ha pedido nadie una prueba. Llegan del botón «Pedir una
            prueba» de la web, y cada una se contesta desde aquí con un código.
          </Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Club</th>
                  <th>Quién</th>
                  <th>Socios</th>
                  <th>Dice</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {solicitudes.map((s) => {
                  const et = ETIQUETA[s.estado];
                  return (
                    <tr
                      key={s.id}
                      style={
                        s.estado === "PENDIENTE" ? undefined : { opacity: 0.7 }
                      }
                    >
                      <td
                        className="pequeno suave"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {fechaHora(s.createdAt)}
                      </td>
                      <td>
                        <strong>{s.nombreClub}</strong>
                        {s.ciudad && (
                          <div className="suave pequeno">{s.ciudad}</div>
                        )}
                      </td>
                      <td className="pequeno">
                        {s.nombre}
                        <div>
                          <a href={`mailto:${s.email}`}>{s.email}</a>
                        </div>
                        {s.telefono && (
                          <div className="suave">{s.telefono}</div>
                        )}
                      </td>
                      <td className="pequeno">
                        {s.sociosAproximados === null ? (
                          <span className="suave">—</span>
                        ) : (
                          <>
                            ~{s.sociosAproximados}
                            <div className="suave">
                              {s.cuotaSugeridaCentimos === null
                                ? "se habla"
                                : `${euros(s.cuotaSugeridaCentimos)}/mes`}
                            </div>
                          </>
                        )}
                      </td>
                      <td
                        className="pequeno suave"
                        style={{ maxWidth: 280, whiteSpace: "pre-wrap" }}
                      >
                        {s.mensaje ?? "—"}
                      </td>
                      <td>
                        <Etiqueta tipo={et.clase}>{et.texto}</Etiqueta>
                        {s.licencia && (
                          <div
                            className="pequeno tabular"
                            style={{ marginTop: 4 }}
                          >
                            {s.licencia.codigo}
                            <div
                              className={
                                s.diasSinCanjear !== null &&
                                s.diasSinCanjear >= diasParaInsistir
                                  ? "ambar"
                                  : "suave"
                              }
                            >
                              {s.licencia.club
                                ? `dio de alta ${s.licencia.club.nombre}`
                                : s.licencia.estado === "USADA"
                                  ? "canjeada"
                                  : s.diasSinCanjear === null
                                    ? "el código ya no vale"
                                    : s.diasSinCanjear === 0
                                      ? "sin canjear todavía · enviado hoy"
                                      : `sin canjear · enviado hace ${plural(s.diasSinCanjear, "día")}`}
                            </div>
                          </div>
                        )}
                        {s.atendidaEn && (
                          <div className="suave pequeno">
                            {fechaHora(s.atendidaEn)}
                            {s.atendidaPor ? ` · ${s.atendidaPor}` : ""}
                          </div>
                        )}
                        {s.notas && (
                          <div className="suave pequeno">{s.notas}</div>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {s.estado === "PENDIENTE" && (
                          <div className="fila" style={{ gap: 4 }}>
                            <button
                              className="boton"
                              onClick={() => setContestando(s)}
                            >
                              Enviar código
                            </button>
                            <button
                              className="boton fantasma"
                              onClick={() => void descartar(s)}
                            >
                              Descartar
                            </button>
                          </div>
                        )}
                        {s.diasSinCanjear !== null && (
                          // Tras la llamada de «¿te llegó?»: el mismo código otra vez.
                          <button
                            className="boton secundario"
                            onClick={() => void reenviar(s)}
                          >
                            Reenviar el código
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {contestando && (
        <DialogoContestar
          solicitud={contestando}
          diasDePrueba={datos.data.diasDePrueba}
          correoConfigurado={correoConfigurado}
          alCerrar={() => setContestando(null)}
          alFallar={setError}
          alHecho={(texto) => {
            setContestando(null);
            setError("");
            setMensaje(texto);
            refrescar();
          }}
        />
      )}
    </div>
  );
}

function DialogoContestar({
  solicitud,
  diasDePrueba,
  correoConfigurado,
  alCerrar,
  alHecho,
  alFallar,
}: {
  solicitud: Solicitud;
  diasDePrueba: number;
  correoConfigurado: boolean;
  alCerrar: () => void;
  alHecho: (mensaje: string) => void;
  alFallar: (mensaje: string) => void;
}) {
  const [datos, setDatos] = useState({
    // La cuota la propone el tramo; si no dijo socios o es de los grandes, se decide aquí.
    cuotaMensualEuros:
      solicitud.cuotaSugeridaCentimos === null
        ? ""
        : String(solicitud.cuotaSugeridaCentimos / 100),
    diasPrueba: String(diasDePrueba),
    notas: "",
  });
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string) =>
    setDatos((d) => ({ ...d, [campo]: valor }));

  return (
    <Modal
      titulo={`Contestar a ${solicitud.nombreClub}`}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                const r = await api<{
                  licencia: { codigo: string };
                  correoEnviado: boolean;
                }>(`/plataforma/solicitudes/${solicitud.id}/atender`, {
                  metodo: "POST",
                  cuerpo: {
                    cuotaMensualEuros: Number(datos.cuotaMensualEuros || 0),
                    diasPrueba: Number(datos.diasPrueba || 0),
                    notas: datos.notas || null,
                  },
                });
                alHecho(
                  r.correoEnviado
                    ? `Código ${r.licencia.codigo} enviado a ${solicitud.email}.`
                    : `Código ${r.licencia.codigo} emitido. El correo no salió: mándaselo tú a ${solicitud.email}.`,
                );
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error
                    ? fallo.message
                    : "No se pudo contestar",
                );
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {enviando
              ? "Enviando..."
              : correoConfigurado
                ? "Emitir y enviar el código"
                : "Emitir el código"}
          </button>
        </>
      }
    >
      <p className="suave" style={{ marginTop: 0 }}>
        {solicitud.nombre} · {solicitud.email}
        {solicitud.sociosAproximados !== null && (
          <> · unos {plural(solicitud.sociosAproximados, "socio")}</>
        )}
      </p>
      <Campo
        etiqueta="Cuota mensual (EUR)"
        ayuda={
          solicitud.cuotaSugeridaCentimos !== null
            ? "La del tramo que le toca por los socios que dijo tener."
            : "No dijo cuántos socios tiene, o son más de 500: decídela tú."
        }
      >
        <input
          type="number"
          min={0}
          step={0.01}
          value={datos.cuotaMensualEuros}
          onChange={(e) => cambiar("cuotaMensualEuros", e.target.value)}
          autoFocus
        />
      </Campo>
      <Campo
        etiqueta="Días de prueba"
        ayuda="Los que anuncia la web, salvo que aquí pongas otros."
      >
        <input
          type="number"
          min={0}
          max={365}
          value={datos.diasPrueba}
          onChange={(e) => cambiar("diasPrueba", e.target.value)}
        />
      </Campo>
      <Campo
        etiqueta="Nota para ti"
        ayuda="Opcional. Se queda en esta lista, no se le manda."
      >
        <input
          value={datos.notas}
          onChange={(e) => cambiar("notas", e.target.value)}
          placeholder="Hablé con él el martes"
        />
      </Campo>
      {!correoConfigurado && (
        <Aviso tipo="atencion">
          Sin correo configurado, el código se emite y te lo enseñamos aquí para
          que se lo mandes tú.
        </Aviso>
      )}
    </Modal>
  );
}
